import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const KEY = "pomata_access_code";
const DEV_KEY = "pomata_device";
const deviceId = () => { let d = localStorage.getItem(DEV_KEY); if (!d) { d = (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now()); localStorage.setItem(DEV_KEY, d); } return d; };
const Ctx = createContext(null);

export function AccessCodeProvider({ children }) {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem(KEY);
    if (!saved) return;
    api.get(`/access-codes/validate?code=${encodeURIComponent(saved)}&device=${deviceId()}`)
      .then((r) => (r.data.valid ? setInfo(r.data) : localStorage.removeItem(KEY)))
      .catch(() => {});
  }, []);

  const refresh = useCallback(() => {
    const saved = localStorage.getItem(KEY);
    if (saved) api.get(`/access-codes/validate?code=${encodeURIComponent(saved)}&device=${deviceId()}`).then((r) => r.data.valid && setInfo(r.data)).catch(() => {});
  }, []);

  const redeem = useCallback(async (code) => {
    const r = await api.get(`/access-codes/validate?code=${encodeURIComponent(code.trim())}&device=${deviceId()}`);
    if (!r.data.valid) return false;
    localStorage.setItem(KEY, r.data.code);
    setInfo(r.data);
    return true;
  }, []);

  const clear = useCallback(() => { localStorage.removeItem(KEY); setInfo(null); }, []);

  const allows = useCallback((itemType, itemId) => {
    if (!info) return false;
    const inScope = info.scope === "all" || (info.scope === "tracks" && itemType === "track") || (info.scope === "packs" && itemType === "pack") || (info.scope === "items" && (info.item_ids || []).includes(itemId));
    if (!inScope) return false;
    const rem = info.remaining?.[itemType === "pack" ? "pack" : "track"];
    if (rem === null || rem === undefined) return true;
    return rem > 0 || (info.redeemed || []).includes(itemId);
  }, [info]);

  const withCode = useCallback((url) => {
    if (!info) return url;
    setTimeout(refresh, 4000);
    return `${url}${url.includes("?") ? "&" : "?"}code=${encodeURIComponent(info.code)}&device=${deviceId()}`;
  }, [info, refresh]);

  const value = useMemo(() => ({ code: info?.code || null, info, redeem, clear, allows, withCode, refresh }), [info, redeem, clear, allows, withCode, refresh]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAccessCode = () => useContext(Ctx) || { code: null, info: null, redeem: async () => false, clear: () => {}, allows: () => false, withCode: (u) => u };
